'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { RAINBOW_GRADIENT, rainbowFrameStyle } from './ledger-rainbow';

const PILLARS = [
  {
    id: 'history',
    name: 'History',
    color: '#F59E0B',
    tagline: 'Where you came from',
    description: 'Heritage, lineage, and moments worth preserving.',
    examples: 'Family archives · Timelines · Cultural records',
    story: "I archived my grandfather's original hospital papers so the story stays on the ledger.",
    author: 'Amara K.',
  },
  {
    id: 'achievement',
    name: 'Achievement',
    color: '#10B981',
    tagline: 'What you earned',
    description: 'Degrees, certifications, and honors verified by institutions.',
    examples: 'Degrees · Certifications · Academic awards',
    story: 'My PhD link is on Ertzyx — no more chasing transcripts.',
    author: 'Dr. Lena M.',
  },
  {
    id: 'performance',
    name: 'Performance',
    color: '#3B82F6',
    tagline: 'What you built',
    description: 'Career milestones and professional proof of work.',
    examples: 'Licenses · Industry awards · Career wins',
    story: 'My business story is verified and permanent on my vault.',
    author: 'Jamal T.',
  },
  {
    id: 'hero',
    name: 'Hero',
    color: '#EF4444',
    tagline: 'What you gave',
    description: 'Courage, service, and leadership that helped others.',
    examples: 'Civic action · Humanitarian work · Community service',
    story: 'A verified record of giving connected two strangers years later.',
    author: 'Priya S.',
  },
  {
    id: 'grit',
    name: 'Grit / Mischief',
    color: '#8B5CF6',
    tagline: 'What shaped you',
    description: 'Resilience and turning points that forged character.',
    examples: 'Comebacks · Hard seasons · Defining choices',
    story: 'Forty rejections, one role — grit became my credential.',
    author: 'Marcus O.',
  },
  {
    id: 'favorites',
    name: 'Favorites / Products',
    color: '#EC4899',
    tagline: 'What you created',
    description: 'Creative work, products, and cultural artifacts you stand behind.',
    examples: 'Designs · Collections · Signature work',
    story: 'Original batik designs archived with timestamps — protected on the ledger.',
    author: 'Aiko N.',
  },
  {
    id: 'news',
    name: 'News',
    color: '#06B6D4',
    tagline: 'What was said about you',
    description: 'Press, features, and public recognition — verified and kept.',
    examples: 'Media features · Press · Public announcements',
    story: 'Forbes feature preserved as full verified story, not a screenshot.',
    author: 'Carlos R.',
  },
];

function HistoryIcon({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="hist-bg" x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
          <stop stopColor="#FDE68A" />
          <stop offset="1" stopColor="#D97706" />
        </linearGradient>
      </defs>
      <rect x="5" y="7" width="14" height="10" rx="1.5" fill="url(#hist-bg)" />
      <rect x="5" y="7" width="14" height="3" rx="1.5" fill="white" fillOpacity="0.18" />
      <ellipse cx="5" cy="12" rx="1.9" ry="5" fill="#F59E0B" />
      <ellipse cx="19" cy="12" rx="1.9" ry="5" fill="#B45309" />
    </svg>
  );
}

function AchievementIcon({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="8" y="20" width="8" height="1.5" rx="0.75" fill="#064E3B" />
      <rect x="11" y="15" width="2" height="3.5" rx="0.7" fill="#047857" />
      <path d="M8.5 4h7L14 14c-.3 1.2-1.1 1.8-2 1.8s-1.7-.6-2-1.8L8.5 4z" fill="#10B981" />
      <path
        d="M12 6.5l.65 1.9H14.5l-1.6 1.15.6 1.9-1.5-1.1-1.5 1.1.6-1.9L9.5 8.4h1.85z"
        fill="white"
        fillOpacity="0.82"
      />
    </svg>
  );
}

function PerformanceIcon({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="3.5" y="13.5" width="4" height="7" rx="1" fill="#3B82F6" />
      <rect x="10" y="9" width="4" height="11.5" rx="1" fill="#60A5FA" />
      <rect x="16.5" y="4.5" width="4" height="16" rx="1" fill="#93C5FD" />
    </svg>
  );
}

function HeroIcon({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 20.5c0 0-7-3.3-7-10.5V4.2L12 2.2l7 2V10c0 7.2-7 10.5-7 10.5z" fill="#EF4444" />
      <path
        d="M12 7.5l.9 2.6h2.7l-2.2 1.6.9 2.7-2.3-1.65-2.3 1.65.9-2.7-2.2-1.6H11.1z"
        fill="white"
        fillOpacity="0.9"
      />
    </svg>
  );
}

function GritIcon({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polygon points="12,3 18.5,8.5 12,10.5 5.5,8.5" fill="#C4B5FD" />
      <polygon points="5.5,8.5 10,20.5 3,13" fill="#8B5CF6" />
      <polygon points="18.5,8.5 14,20.5 21,13" fill="#6D28D9" />
    </svg>
  );
}

function FavoritesIcon({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 20s-8-5-8-10.5A4.5 4.5 0 0112 7.5a4.5 4.5 0 018 2c0 5.5-8 10.5-8 10.5z"
        fill="#EC4899"
      />
    </svg>
  );
}

function NewsIcon({ size }: { size: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="16.5" r="2" fill="#06B6D4" />
      <line
        x1="12"
        y1="18.5"
        x2="12"
        y2="21"
        stroke="#0891B2"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M8.5 14 A4.5 4.5 0 0 1 15.5 14"
        stroke="#06B6D4"
        strokeWidth="1.8"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function PillarIcon({ id, size = 20 }: { id: string; size?: number }) {
  switch (id) {
    case 'history':
      return <HistoryIcon size={size} />;
    case 'achievement':
      return <AchievementIcon size={size} />;
    case 'performance':
      return <PerformanceIcon size={size} />;
    case 'hero':
      return <HeroIcon size={size} />;
    case 'grit':
      return <GritIcon size={size} />;
    case 'favorites':
      return <FavoritesIcon size={size} />;
    case 'news':
      return <NewsIcon size={size} />;
    default:
      return <HistoryIcon size={size} />;
  }
}

export default function SevenPillarsSection() {
  const [active, setActive] = useState(0);
  const pillar = PILLARS[active];

  return (
    <section
      id="pillars"
      className="py-16 px-4 sm:px-6"
      style={{ background: 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)' }}
    >
      <style>{`
        .pillar-tab {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .pillar-tab-inactive:hover {
          transform: scale(1.04);
          background: rgba(255,255,255,0.08) !important;
        }
        .pillar-panel {
          animation: pillar-panel-in 0.28s ease both;
        }
        @keyframes pillar-panel-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-sm font-semibold text-white/50 mb-2">The foundation</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            7 pillars. One life story.
          </h2>
          <p className="text-slate-400 text-base max-w-xl mx-auto">
            Organize achievements and stories across seven clear chapters — verified when it
            matters.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {PILLARS.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActive(i)}
              className={`pillar-tab flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border ${
                active === i ? '' : 'pillar-tab-inactive'
              }`}
              style={
                active === i
                  ? { background: p.color, color: '#fff', border: `1px solid ${p.color}` }
                  : {
                      background: 'rgba(255,255,255,0.04)',
                      color: 'rgba(255,255,255,0.55)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }
              }
            >
              <PillarIcon id={p.id} size={12} />
              <span className="hidden sm:inline">{p.name}</span>
              <span className="sm:hidden">{p.name.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        <div
          key={pillar.id}
          className="pillar-panel rounded-2xl p-[3px]"
          style={rainbowFrameStyle()}
        >
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-[14px] overflow-hidden"
            style={{ background: 'rgba(15,23,42,0.96)' }}
          >
            <div className="p-6 sm:p-8 border-b lg:border-b-0 lg:border-r border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: `${pillar.color}30`, border: `1px solid ${pillar.color}50` }}
                >
                  <PillarIcon id={pillar.id} size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{pillar.name}</h3>
                  <p className="text-sm font-medium" style={{ color: pillar.color }}>
                    {pillar.tagline}
                  </p>
                </div>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">{pillar.description}</p>
              <p className="text-xs text-slate-500 mb-5">{pillar.examples}</p>
              <Link href="/register">
                <button
                  type="button"
                  className="px-5 py-2.5 rounded-xl text-sm font-bold text-white"
                  style={{ background: pillar.color }}
                >
                  Start in {pillar.name.split('/')[0].trim()}
                </button>
              </Link>
            </div>

            <div className="p-6 sm:p-8 flex flex-col justify-center">
              <p
                className="text-xs font-bold uppercase tracking-wider mb-2"
                style={{ color: pillar.color }}
              >
                Member story
              </p>
              <p className="text-white text-base leading-relaxed mb-3">
                &ldquo;{pillar.story}&rdquo;
              </p>
              <p className="text-sm text-slate-500">— {pillar.author}</p>
            </div>
          </div>
        </div>

        <div className="text-center mt-8">
          <Link href="/register">
            <button
              type="button"
              className="px-8 py-3.5 rounded-2xl font-bold text-slate-950 text-base shadow-lg"
              style={{ background: RAINBOW_GRADIENT }}
            >
              Start your ledger free
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
}
